import { useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";

export default function RecipeEditor({ recipe, onClose, onSave, onDelete }) {
  const isNew = !recipe.id;
  const [name, setName] = useState(recipe.name || "");
  const [unit, setUnit] = useState(recipe.unit || "buc");
  const [ingredients, setIngredients] = useState(
    (recipe.ingredients || []).map((i) => ({ ...i }))
  );

  const setIng = (idx, key, val) => {
    setIngredients((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };

  const addRow = () => {
    setIngredients((prev) => [...prev, { name: "", qty_per_unit: 0 }]);
  };

  const removeRow = (idx) => {
    setIngredients((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const cleaned = ingredients
      .map((i) => ({ name: (i.name || "").trim(), qty_per_unit: parseFloat(i.qty_per_unit) || 0 }))
      .filter((i) => i.name.length > 0);
    onSave({ ...recipe, name: name.trim(), unit, ingredients: cleaned });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl border border-neutral-200 shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="recipe-editor-modal"
      >
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-neutral-900">
              {isNew ? "Rețetă nouă" : "Editare rețetă"}
            </h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Cantitățile sunt în kg per unitate (buc / kg / tavă)
            </p>
          </div>
          <button
            data-testid="editor-close"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-900 p-1 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto thin-scroll flex-1">
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="col-span-2">
              <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 mb-1 block">Denumire</label>
              <input
                data-testid="editor-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="Ex: Merdenele"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500 mb-1 block">U.M.</label>
              <select
                data-testid="editor-unit-select"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
              >
                <option value="buc">buc</option>
                <option value="kg">kg</option>
                <option value="tavă">tavă</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] font-semibold tracking-wider uppercase text-neutral-500">Ingrediente</label>
            <button
              data-testid="editor-add-ingredient"
              onClick={addRow}
              className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 font-semibold"
            >
              <Plus className="w-3.5 h-3.5" /> Adaugă
            </button>
          </div>
          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 text-left text-[10px] font-semibold tracking-wider uppercase text-neutral-500">
                  <th className="px-3 py-2 w-10">#</th>
                  <th className="px-3 py-2">Materie primă</th>
                  <th className="px-3 py-2 w-32 text-right">Kg / unitate</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {ingredients.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-xs text-neutral-400 py-6">Nicio materie primă. Apasă "Adaugă".</td>
                  </tr>
                )}
                {ingredients.map((ing, idx) => (
                  <tr key={idx} className="border-t border-neutral-100">
                    <td className="px-3 py-1.5 text-xs text-neutral-400">{idx + 1}</td>
                    <td className="px-3 py-1.5">
                      <input
                        data-testid={`ing-name-${idx}`}
                        type="text"
                        value={ing.name}
                        onChange={(e) => setIng(idx, "name", e.target.value)}
                        placeholder="ex: faina alba"
                        className="w-full rounded border border-neutral-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        data-testid={`ing-qty-${idx}`}
                        type="number"
                        step="any"
                        min="0"
                        value={ing.qty_per_unit}
                        onChange={(e) => setIng(idx, "qty_per_unit", e.target.value)}
                        className="w-full rounded border border-neutral-200 px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <button
                        data-testid={`ing-remove-${idx}`}
                        onClick={() => removeRow(idx)}
                        className="text-neutral-400 hover:text-red-600 p-1 rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-neutral-200 flex items-center justify-between bg-neutral-50/50">
          <div>
            {!isNew && (
              <button
                data-testid="editor-delete"
                onClick={onDelete}
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium"
              >
                <Trash2 className="w-3.5 h-3.5" /> Șterge rețeta
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="editor-cancel"
              onClick={onClose}
              className="rounded-md border border-neutral-200 bg-white px-4 py-2 text-xs font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
            >
              Anulează
            </button>
            <button
              data-testid="editor-save"
              onClick={handleSave}
              className="rounded-md bg-amber-600 hover:bg-amber-700 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors"
            >
              Salvează
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
